import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DownloadIcon from "@mui/icons-material/Download";

import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
} from "recharts";

const SalaryDistribution = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rangeAnchor, setRangeAnchor] = useState(null);
  const [filter, setFilter] = useState("Monthly");

  const open = Boolean(anchorEl);
  const openRange = Boolean(rangeAnchor);

  // 🔥 dynamic data generator
  const generateData = () => [
    { range: "30k-50k", value: Math.floor(Math.random() * 300) + 100 },
    { range: "50k-80k", value: Math.floor(Math.random() * 500) + 200 },
    { range: "80k-120k", value: Math.floor(Math.random() * 400) + 150 },
    { range: "120k-150k", value: Math.floor(Math.random() * 150) + 50 },
    { range: "150k-180k", value: Math.floor(Math.random() * 100) + 20 },
  ];

  const [data, setData] = useState(generateData());

  // 🔥 auto update every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setData(generateData());
    }, 3000);

    return () => clearInterval(interval);
  }, [filter]);

  const total = data.reduce((sum, d) => sum + d.value, 0);

  // 🔥 custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: "#fff",
            padding: "10px",
            borderRadius: "8px",
            boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
            fontSize: "13px",
          }}
        >
          <strong>{payload[0].payload.range}</strong>
          <br />
          Income Range: {payload[0].value}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="h-100" sx={{ borderRadius: "3px",    border: "1px solid #eee",
 }}>
      <CardContent>

        {/* HEADER */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={600}>
            Salary Range Distribution
          </Typography>

          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <MoreVertIcon />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: {
                borderRadius: "10px",
                mt: 1,
              },
            }}
          >
            {["Monthly", "Weekly", "Today"].map((item) => (
              <MenuItem
                key={item}
                onClick={() => {
                  setFilter(item);
                  setAnchorEl(null);
                }}
              >
                {item}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* AVERAGE */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
        >
          <Typography color="text.secondary">
            Average Salary
          </Typography>

          <Typography fontWeight={700} fontSize="24px">
            $78,450
          </Typography>
        </Box>

        {/* GRAPH (Vertical Range Style) */}
        <Box mt={3}>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={data}>
              <XAxis hide />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />

              <Bar
                dataKey="value"
                fill="#ff6b1a"
                radius={[10, 10, 10, 10]}
                barSize={20}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>

        {/* RANGE HEADER */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
        >
          <Typography fontWeight={600}>Range</Typography>

          <Box
            onClick={(e) => setRangeAnchor(e.currentTarget)}
            sx={{ cursor: "pointer", fontWeight: 500 }}
          >
            Last 7 Days <KeyboardArrowDownIcon fontSize="small" />
          </Box>

          <Menu
            anchorEl={rangeAnchor}
            open={openRange}
            onClose={() => setRangeAnchor(null)}
          >
            {["Today", "Weekly", "Monthly"].map((item) => (
              <MenuItem
                key={item}
                onClick={() => {
                  setFilter(item);
                  setRangeAnchor(null);
                }}
              >
                {item}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* RANGE LIST */}
        {data.map((item, i) => {
          const percent = ((item.value / total) * 100).toFixed(1);

          return (
            <Box key={i} mt={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography>${item.range}</Typography>
                <Typography fontWeight={600}>
                  {item.value} ({percent}%)
                </Typography>
              </Box>

              <Box
                sx={{
                  height: "6px",
                  background: "#eee",
                  borderRadius: "10px",
                  mt: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: `${percent}%`,
                    height: "100%",
                    background: "#ff6b1a",
                    borderRadius: "10px",
                  }}
                />
              </Box>
            </Box>
          );
        })}

        {/* FOOTER */}
        <Box textAlign="center" mt={3}>
          <Typography fontWeight={500} sx={{ cursor: "pointer" }}>
            View Salary Report in realtime ↗
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SalaryDistribution;